import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Select,
  VStack,
  Heading,
  Card,
  CardBody,
  useToast,
  useColorModeValue,
  Switch,
  Text,
  HStack,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { useUser } from "../../contexts/UserContext";
import { useLocation } from "react-router-dom";
import { useFeatureToggle } from "../../contexts/FeatureToggleContext";
import { useTranslation } from "react-i18next";

const Preferences = () => {
  const { t, i18n } = useTranslation();
  const { settings, updateSettings, isLoading, user } = useUser();
  const { isFeatureEnabled } = useFeatureToggle();
  const toast = useToast();
  const location = useLocation();
  const bgColor = useColorModeValue("white", "gray.800");

  const ENABLE_THEME_TOGGLE = isFeatureEnabled("system_theme");
  const isHubUser = location.pathname.startsWith("/hub");

  const isTourUser = user?.user_type === "tour";

  const [theme, setTheme] = useState(settings?.theme ?? 'light');
  const [language, setLanguage] = useState(settings?.language ?? 'en');
  const [emailNotifications, setEmailNotifications] = useState(
    settings?.emailNotifications ?? true
  );

  useEffect(() => {
    if (!settings) return;
    setTheme(settings.theme ?? 'light');
    setLanguage(settings.language ?? 'en');
    setEmailNotifications(settings.emailNotifications ?? true);
  }, [settings]);

  const handleSave = async () => {
    if (isTourUser) {
      toast({
        title: t("preferences.demo_mode"),
        description: t("preferences.demo_mode_desc"),
        status: "info",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    const success = await updateSettings({ theme, language, emailNotifications });

    toast({
      title: success
        ? t("preferences.preferences_updated")
        : "Error",
      description: success
        ? t("preferences.preferences_saved")
        : t("preferences.preferences_error"),
      status: success ? "success" : "error",
      duration: 5000,
      isClosable: true,
    });
  };

  if (isLoading) {
    return (
      <Center minH="60vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box mx="auto" p={6} maxW="container.md">
      <Heading as="h2" size="lg" mb={6} textAlign="center">
        {isHubUser ? t("preferences.title_org") : t("preferences.title_user")}
      </Heading>

      <Card bg={bgColor} shadow="md" borderRadius="lg">
        <CardBody>
          <VStack spacing={5} align="stretch">
            <FormControl>
              <FormLabel>{t("preferences.language")}</FormLabel>
              <Select
                value={language}
                onChange={(e) => {
                  const selectedLang = e.target.value;
                  setLanguage(selectedLang);
                  i18n.changeLanguage(selectedLang); // 🔁 Apply immediately
                  localStorage.setItem('language', selectedLang); // 💾 Store locally
                }}
              >
                <option value="en">English</option>
                <option value="sv">Svenska</option>
              </Select>
            </FormControl>

            {ENABLE_THEME_TOGGLE && (
              <FormControl>
                <FormLabel>{t("preferences.theme")}</FormLabel>
                <Select value={theme} onChange={(e) => setTheme(e.target.value)}>
                  <option value="light">{t("preferences.light_mode")}</option>
                  <option value="dark">{t("preferences.dark_mode")}</option>
                </Select>
              </FormControl>
            )}

            <FormControl>
              <HStack justifyContent="space-between">
                <FormLabel mb="0">{t("preferences.email_notifications")}</FormLabel>
                <Switch
                  isChecked={emailNotifications}
                  onChange={() => setEmailNotifications((prev) => !prev)}
                />
              </HStack>
              <Text fontSize="sm" color="gray.500">
                {t("preferences.email_notifications_desc")}
              </Text>
            </FormControl>

            <Divider />

            <Button onClick={handleSave} colorScheme="green" size="lg" variant="outline">
              {t("preferences.save_changes")}
            </Button>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default Preferences;
